import React from "react";
import { Table, Tag, Button, Tooltip, Space, Progress } from "antd";
import {
  EyeOutlined,
  SendOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const BudgetTable = ({
  budgets,
  loading,
  onViewDetails,
  onSubmit,
  onApprove,
  onReject,
  onDelete,
}) => {
  const termLabels = { T1: "Term 1", T2: "Term 2", T3: "Term 3" };
  const getStatusColor = (status) => {
    const statusColors = {
      Draft: "default",
      Submitted: "blue",
      Approved: "green",
      Active: "green",
      Closed: "gray",
    };
    return statusColors[status] || "default";
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const columns = [
    {
      title: "Item",
      dataIndex: "departmentName",
      key: "departmentName",
      width: 180,
      render: (name, record) => (
        <a onClick={() => onViewDetails(record)}>{name || "-"}</a>
      ),
      sorter: (a, b) =>
        (a.departmentName || "").localeCompare(b.departmentName || ""),
    },
    {
      title: "Fiscal Year",
      dataIndex: "fiscalYear",
      key: "fiscalYear",
      width: 100,
      sorter: (a, b) => (a.fiscalYear || "").localeCompare(b.fiscalYear || ""),
    },
    {
      title: "Term",
      dataIndex: "term",
      key: "term",
      width: 90,
      render: (term, record) => termLabels[term] || record.quarter || "-",
      filters: [
        { text: "Term 1", value: "T1" },
        { text: "Term 2", value: "T2" },
        { text: "Term 3", value: "T3" },
      ],
      onFilter: (value, record) => record.term === value,
    },
    {
      title: "Budget Code",
      dataIndex: "budgetCode",
      key: "budgetCode",
      width: 130,
      render: (code) => code || "-",
      sorter: (a, b) => (a.budgetCode || "").localeCompare(b.budgetCode || ""),
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 140,
      align: "right",
      render: (amount) => (
        <span style={{ fontWeight: 600 }}>{formatCurrency(amount)}</span>
      ),
      sorter: (a, b) =>
        parseFloat(a.totalAmount || 0) - parseFloat(b.totalAmount || 0),
    },
    {
      title: "Spent",
      dataIndex: "amountSpent",
      key: "amountSpent",
      width: 130,
      align: "right",
      render: (amount) => (
        <span style={{ color: "#ff4d4f" }}>{formatCurrency(amount)}</span>
      ),
    },
    {
      title: "Committed",
      dataIndex: "amountCommitted",
      key: "amountCommitted",
      width: 130,
      align: "right",
      render: (amount) => (
        <span style={{ color: "#faad14" }}>{formatCurrency(amount)}</span>
      ),
    },
    {
      title: "Available",
      dataIndex: "availableBalance",
      key: "availableBalance",
      width: 130,
      align: "right",
      render: (amount) => (
        <span style={{ fontWeight: 600, color: "#52c41a" }}>
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: "Utilization",
      key: "utilization",
      width: 150,
      render: (_, record) => {
        const total = parseFloat(record.totalAmount) || 0;
        const spent = parseFloat(record.amountSpent) || 0;
        const committed = parseFloat(record.amountCommitted) || 0;
        const used = spent + committed;
        const percent = total > 0 ? Math.round((used / total) * 100) : 0;
        return (
          <Progress
            percent={percent}
            size="small"
            status={
              percent >= 90 ? "exception" : percent >= 70 ? "active" : "normal"
            }
          />
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status === "Approved" ? "Pending activation (legacy)" : status}
        </Tag>
      ),
      filters: [
        { text: "Draft", value: "Draft" },
        { text: "Submitted", value: "Submitted" },
        { text: "Active", value: "Active" },
        { text: "Closed", value: "Closed" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Actions",
      key: "actions",
      width: 220,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => onViewDetails(record)}
            />
          </Tooltip>
          {record.status === "Draft" && (
            <Tooltip title="Submit for Approval">
              <Button
                type="link"
                icon={<SendOutlined />}
                style={{ color: "#1890ff" }}
                onClick={() => onSubmit(record)}
              />
            </Tooltip>
          )}
          {record.status === "Submitted" && (
            <>
              <Tooltip title="Approve & Activate Budget">
                <Button
                  type="link"
                  icon={<CheckCircleOutlined />}
                  style={{ color: "#52c41a" }}
                  onClick={() => onApprove(record)}
                />
              </Tooltip>
              <Tooltip title="Reject Budget">
                <Button
                  type="link"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => onReject(record)}
                />
              </Tooltip>
            </>
          )}
          {record.status === "Approved" && (
            <Tooltip title="Activate legacy approved budget">
              <Button
                type="link"
                icon={<CheckCircleOutlined />}
                style={{ color: "#52c41a" }}
                onClick={() => onApprove(record)}
              />
            </Tooltip>
          )}
          {record.status === "Draft" && (
            <Tooltip title="Delete">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onDelete(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={budgets}
      rowKey="budgetId"
      loading={loading}
      scroll={{ x: 1400 }}
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} budgets`,
      }}
    />
  );
};

export default BudgetTable;
